<script setup lang="ts">
import { reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from "vue-router";

import validators from '@/utils/validators.ts';
import CardComponent from '@/components/ui/CardComponent.vue';
import AuthService from '@/services/auth/authService.ts';

import type { ValidationRule } from '@/types/utils/validators.type.ts';
import type { ForgotPasswordRequest } from '@/types/services/auth/auth.type.ts';
import AppLogoComponent from '@/components/ui/AppLogoComponent.vue';

const { t } = useI18n()
const router = useRouter()

//--- Define page content ---//
const loading = ref<boolean>(false)

//--- Forgot Password form ---//
const formValid = ref<boolean>(false)
const emailRules = ref<ValidationRule[]>([...validators.emailRules])
const form = reactive({
	email: '',
})

const sendResetEmail = async (): Promise<void> => {
	loading.value = true

	const user : ForgotPasswordRequest = {
		email: form.email,
	}

	try {
		await AuthService.forgotPassword(user);
		await router.push('/reset-password-sent')
	} finally {
		loading.value = false
	}
}
</script>

<template>
	<v-container fluid class="h-screen w-full d-flex justify-center align-center">
		<CardComponent>
			<template #title>
				<div class="d-flex align-center justify-center my-4">
          <AppLogoComponent />
				</div>
			</template>
			<template #item>
				<v-row>
					<v-col cols="auto">
						<RouterLink to="/login" class="d-flex align-center">
							<v-icon icon="PhArrowLeft" size="medium" class="mr-1" />
							{{ t('common.buttons.back') }}
						</RouterLink>
					</v-col>
					<v-col cols="12">
						<h3>{{ t('views.forgotPassword.title') }}</h3>
					</v-col>
					<v-col cols="12">
						<p class="text-subtitle-2">{{ t('views.forgotPassword.description') }}</p>
					</v-col>
				</v-row>
				<v-form v-model="formValid" @submit.prevent="sendResetEmail">
					<v-row class="py-1">
						<v-col cols="12">
							<v-text-field
								v-model="form.email"
								:label="t('common.inputs.email.label')"
								:placeholder="t('common.inputs.email.placeholder')"
								:rules="emailRules"
								autocomplete="email"
								hide-details="auto"
							/>
						</v-col>
					</v-row>
					<v-row>
						<v-col cols="12">
							<v-btn
                :disabled="!formValid || loading"
                :loading="loading"
                block
                type="submit"
              >
								{{ t('views.forgotPassword.buttons.sendMail') }}
								<template #append>
									<v-icon icon="PhPaperPlaneRight"/>
								</template>
							</v-btn>
						</v-col>
					</v-row>
				</v-form>
			</template>
		</CardComponent>
	</v-container>
</template>
